import { CurrencyBalances } from '@olv/ui-shared/models/wallet.model';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { addDecimalStrings, toDecimalString } from './decimal-places.util';

export interface AssetEntry extends CurrencyBalances {
  label: string;
  iconKey: string | null;
  networkIconKey: string | null;
}

export interface AssetBalance {
  asset: string;
  name: string;
  iconKey: string | null;
  precision: number;
  total: string;
  entries: AssetEntry[];
}

export const FALLBACK_COIN_ICON = 'shared-assets/images/select-blockchain.png';

export function useFallbackIcon(event: Event): void {
  const img = event.target as HTMLImageElement;
  if (!img.src.endsWith(FALLBACK_COIN_ICON)) img.src = FALLBACK_COIN_ICON;
}

export function coinIcon(iconKey: string | null | undefined): string {
  return iconKey ? `shared-assets/images/${iconKey.toLowerCase()}-icon.svg` : FALLBACK_COIN_ICON;
}

export function symbolIcon(symbol: string, metadata: NetworkMetadataService): string {
  const key = symbol.toUpperCase();
  const iconKey =
    metadata.currencies().find((currency) => currency.symbol.toUpperCase() === key)?.iconKey ??
    metadata.networks().find((network) => network.code.toUpperCase() === key)?.iconKey;
  return coinIcon(iconKey);
}

export function balanceIcons(
  currencyId: string | undefined,
  label: string,
  metadata: NetworkMetadataService,
): string[] {
  const currency = currencyId ? metadata.currency(currencyId) : undefined;
  if (!currency) {
    const [coin, ...suffixIcons] = label.split('-').map((part) => symbolIcon(part, metadata));
    return [coin, ...suffixIcons.filter((icon) => icon !== FALLBACK_COIN_ICON)];
  }

  const network = currency.isNative ? undefined : metadata.networkOf(currency);
  return network ? [coinIcon(currency.iconKey), coinIcon(network.iconKey)] : [coinIcon(currency.iconKey)];
}

export function balanceIconClass(
  currencyId: string | undefined,
  label: string,
  metadata: NetworkMetadataService,
): string {
  const iconKey = currencyId ? metadata.currency(currencyId)?.iconKey : undefined;
  return (
    iconKey ??
    label
      .split('-')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
  );
}

export function groupBalancesByAsset(balances: CurrencyBalances[], metadata: NetworkMetadataService): AssetBalance[] {
  const byAsset = new Map<string, AssetBalance>();

  for (const balance of balances) {
    const currency = metadata.currency(balance.currencyId);
    const network = currency && !currency.isNative ? metadata.networkOf(currency) : undefined;
    const asset = currency?.symbol ?? balance.currencySymbol;
    const nativeNetwork = currency ? undefined : metadata.networks().find((n) => n.nativeCurrencyId === balance.currencyId);
    const entry: AssetEntry = {
      ...balance,
      label: currency?.display ?? balance.currencySymbol,
      iconKey: currency?.iconKey ?? nativeNetwork?.iconKey ?? null,
      networkIconKey: network?.iconKey ?? null,
    };
    const group = byAsset.get(asset);

    if (group) {
      group.entries.push(entry);
      group.total = addDecimalStrings(group.total, toDecimalString(balance.totalAmountInCurrency) ?? '0');
      group.precision = Math.max(group.precision, balance.currencyDisplayPrecision);
    } else {
      byAsset.set(asset, {
        asset,
        name: currency?.name ?? balance.currencyName ?? '',
        iconKey: entry.iconKey,
        precision: balance.currencyDisplayPrecision,
        total: toDecimalString(balance.totalAmountInCurrency) ?? '0',
        entries: [entry],
      });
    }
  }

  return [...byAsset.values()];
}
